import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HelpModal from '../HelpModal';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import HEXASLogo from '../assets/Logos/HEXASLogo.jpg';
import GuidedPractice01FormAudio from '../assets/assets/listening/GuidedPractice01Form.mp3';

import Class09GuidedPractice01Image from '../assets/assets/listening/Class09GuidedPractice01.jpg';

import Class09GuidedPractice02Image from '../assets/assets/listening/Class09GuidedPractice02.jpg';

import showgunImg from '../assets/assets/reading/underseaturbine.png';

import { useTextHighlight } from '../utils/highlightUtils';

const Reading_Class_01 = () => {
    const navigate = useNavigate();

    const [showHelp, setShowHelp] = useState(false);

    const questionRefs = useRef([]);
    const rightColRef = useRef(null);

    // Make sure your answers state exists
    const [answers, setAnswers] = useState(Array(10).fill(''));
    const [currentQuestion, setCurrentQuestion] = useState(1);
    const onNext = () => {
        setCurrentQuestion(prev => Math.min(prev + 1, 10));
    };

    // Move availableWords and DropBox here
    const initialWords = [
      "solution", "partnership", "destruction", "exaggeration", "cleanliness", "regulations", "illness", "nutrition"
    ];
    const [availableWords, setAvailableWords] = useState(initialWords);

    function DropBox({ idx }) {
      const placeholder = (idx - 16 + 17).toString();
      return (
        <span
          className="inline-block align-middle mx-1 w-[100px] min-h-[28px] bg-[#E0F2FE] border border-[#7DD3FC] rounded px-2 py-1 shadow cursor-pointer text-center select-none"
          style={{ lineHeight: '26px', verticalAlign: 'middle' }}
          onDrop={e => {
            e.preventDefault();
            const word = e.dataTransfer.getData('text/plain');
            if (!word) return;
            const newAnswers = [...answers];
            const prev = newAnswers[idx];
            if (prev && prev !== word) setAvailableWords(prevWords => [...prevWords, prev]);
            newAnswers[idx] = word;
            setAnswers(newAnswers);
            setAvailableWords(prevWords => prevWords.filter(w => w !== word));
          }}
          onDragOver={e => e.preventDefault()}
          onClick={() => {
            if (answers[idx]) setAvailableWords(prevWords => [...prevWords, answers[idx]]);
            const newAnswers = [...answers];
            newAnswers[idx] = '';
            setAnswers(newAnswers);
          }}
          draggable={!!answers[idx]}
          onDragStart={e => {
            if (answers[idx]) {
              e.dataTransfer.setData('text/plain', answers[idx]);
              setAvailableWords(prevWords => [...prevWords, answers[idx]]);
              const newAnswers = [...answers];
              newAnswers[idx] = '';
              setAnswers(newAnswers);
            }
          }}
        >
          {answers[idx] ? (
            answers[idx]
          ) : (
            <span className="font-bold text-black select-none">{placeholder}</span>
          )}
        </span>
      );
    }

    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);

    const handleStartTest = () => {
        navigate(`/guided-practice-01-form`);
    };

    const handlePlayPause = () => {
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleTimeUpdate = () => {
        if (!audioRef.current) return;
        setCurrentTime(audioRef.current.currentTime);
    };

    const handleLoadedMetadata = () => {
        if (!audioRef.current) return;
        setDuration(audioRef.current.duration);
    };

    const handleSeek = (e) => {
        if (!audioRef.current) return;
        const time = parseFloat(e.target.value);
        audioRef.current.currentTime = time;
        setCurrentTime(time);
    };

    const handleVolumeChange = (e) => {
        if (!audioRef.current) return;
        const vol = parseFloat(e.target.value);
        setVolume(vol);
        audioRef.current.volume = vol;
        setIsMuted(vol === 0);
    };

    const handleMute = () => {
        if (!audioRef.current) return;
        if (isMuted) {
            audioRef.current.muted = false;
            setIsMuted(false);
            audioRef.current.volume = volume;
        } else {
            audioRef.current.muted = true;
            setIsMuted(true);
        }
    };

    // Timer state
    const [timeRemaining, setTimeRemaining] = useState(30 * 60); // 30 minutes in seconds
    const [showSeconds, setShowSeconds] = useState(false);

    // Timer effect
    useEffect(() => {
        const timer = setInterval(() => {
            setTimeRemaining(prev => {
                if (prev <= 0) {
                    clearInterval(timer);
                    navigate('/');  // Redirect to home
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [navigate]);

    // Format time function
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;

        if (showSeconds) {
            return `${mins} : ${secs.toString().padStart(2, '0')} remaining`;
        }
        return `${mins} minutes remaining`;
    };

    const GreenCheck = () => (
        <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="inline-block"
        >
            <path
                d="M7 11L9 13.5L16 7"
                stroke="#7DD77D"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );

    const highlightRef = useRef(null);
    useTextHighlight(highlightRef);

    return (
        <div className="min-h-screen bg-white flex flex-col font-[Arial]">
            {/* Help Modal */}
            <HelpModal
                isOpen={showHelp}
                onClose={() => setShowHelp(false)}
            />

            {/* Top Bar (fixed) */}
            <div className="sticky top-0 z-30 bg-white">

                <div className="relative h-[62px] bg-[#F4C3C3] border-b border-black flex items-center justify-between px-4">
                    {/* Left + Center combined on left side */}
                    <div className="flex flex-col justify-center space-y-0.5 pl-6">
                        <span className="text-[14px] text-left text-black font-bold leading-tight">
                            HEXA'S Education
                        </span>
                        <div className="flex items-center space-x-6 text-[14px] text-black font-medium">
                            <span
                                onMouseEnter={() => setShowSeconds(true)}
                                onMouseLeave={() => setShowSeconds(false)}
                                className="cursor-pointer"
                            >
                                {formatTime(timeRemaining)}
                            </span>
                        </div>
                    </div>

                    {/* Right: Icons and Finish Button */}
                    <div className="flex items-center space-x-5">
                        <button
                            onClick={() => navigate('/')}
                            className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
                        >
                            Back Home
                        </button>
                        <button
                            onClick={() => setShowHelp(true)}
                            className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
                        >
                            Finish test
                        </button>

                        {/* Icons container with larger size and spacing */}
                        <div className="flex items-center space-x-8 pl-2 pr-2">
                            {/* WiFi Icon */}
                            <div className="w-10 h-10 flex items-center justify-center">
                                <svg className="w-9 h-9 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 010.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.24 0 1 1 0 01-1.415-1.415 5 5 0 017.07 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>

                            {/* Notification Bell Icon */}
                            <div className="w-10 h-10 flex items-center justify-center">
                                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                                </svg>
                            </div>

                            {/* Hamburger Menu Icon */}
                            <div className="w-10 h-10 flex items-center justify-center">
                                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>

                            {/* Pen/Edit Icon */}
                            <div className="w-10 h-10 flex items-center justify-center">
                                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="relative bg-[#F4F7FA] shadow-sm border border-black rounded-lg mx-12 mt-4 mb-0 sticky top-[32px] z-20 flex items-center justify-between">
                    {/* Left: Part Title */}
                    <div className="flex-1 px-4 py-2 text-left">
                        <h2 className="text-[23px] text-black">CD Class 04</h2>
                        <h1 className="text-[18px] text-black">CD IELTS Reading Interface Practice (Summary & Completion)

</h1>
                    </div>

                    {/* Center: Absolutely Positioned Logo */}
                    <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        <img
                            src={HEXASLogo}
                            alt="HEXA'S Logo"
                            className="h-[50px] object-contain"
                        />
                    </div>

                    
                </div>
            </div>

            {/* Main content (scrollable) */}
            <div className="flex-1 flex flex-col items-center justify-start w-full">
                {/* Two-Column Main Content Area */}
                <div ref={highlightRef}
                    className="flex flex-row w-full overflow-hidden -mb-16 mt-2 px-12 pb-20"
                    style={{ height: 'calc(100vh - 180px)' }}
                >
                    {/* Left: Reading Passage */}
                    <div className="bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 w-[49%] h-full max-h-full overflow-y-auto">
                        <div className="mb-8">
                            <h2 className="text-[16px] font-bold mb-4 text-center">Space: The Final Archaeological Frontier</h2>
                            <h3 className="text-[14px] font-semibold mb-6 text-center italic">
                                Space travel may still have a long way to go, but the notion of archaeological research and heritage management in space is already concerning scientists and environmentalists.
                            </h3>

                            {/* Remaining paragraphs - left aligned */}
                            <div className="text-[14px] space-y-4 text-left">
                                <p>
                                    In 1993, University of Hawaii's anthropologist Ben Finney, who for much of his career has studied the technology once used by Polynesians to colonize islands in the Pacific, suggested that it would not be premature to begin thinking about the archaeology of Russian and American aerospace sites on the Moon and Mars. Finney pointed out that just as today's scholars use archaeological records to investigate how Polynesians diverged culturally as they explored the Pacific, archaeologists will someday study off-Earth sites to trace the development of humans in space. He realized that it was unlikely anyone would be able to conduct fieldwork in the near future, but he was convinced that one day such work would be done.
                                </p>
                                <p>
                                    There is a growing awareness, however, that it won't be long before both corporate adventurers and space tourists reach the Moon and Mars. There is a wealth of important archaeological sites from the history of space exploration on the Moon and Mars and measures need to be taken to protect these sites. In addition to the threat from profit-seeking corporations, scholars cite other potentially destructive forces such as souvenir hunting and unmonitored scientific sampling, as has already occurred in explorations of remote polar regions. Already in 1999 one company was proposing a robotic lunar rover mission beginning at the site of Tranquillity Base and rumbling across the Moon from one archaeological site to another, from the wreck of the Ranger S probe to Apollo 17's landing site. The mission, which would leave vehicle tyre-marks all over some of the most famous sites on the Moon, was promoted as a form of theme-park entertainment.
                                </p>
                                <p>
                                    According to the vaguely worded United Nations Outer Space Treaty of 1967, what it terms 'space junk' remains the property of the country that sent the craft or probe into space. But the treaty doesn't explicitly address protection of sites like Tranquillity Base, and equating the remains of human exploration of the heavens with 'space junk' leaves them vulnerable to scavengers. Another problem arises through other international treaties proclaiming that land in space cannot be owned by any country or individual. This presents some interesting dilemmas for the aspiring manager of extraterrestrial cultural resources. Does the US own Neil Armstrong's famous first footprints on the Moon but not the lunar dust in which they were recorded? Surely those footprints are as important in the story of human development as those left by hominids at Laetoli, Tanzania. But unlike the Laetoli prints, which have survived for 3.5 million years encased in cement-like ash, those at Tranquillity Base could be swept away with a casual brush of a space tourist's hand. To deal with problems like these, it may be time to look to innovative international administrative structures for the preservation of historic remains on the new frontier.
                                </p>
                                <p>
                                    The Moon, with its wealth of sites, will surely be the first destination of archaeologists trained to work in space. But any young scholars hoping to claim the mantle of history's first lunar archaeologist will be disappointed. That distinction is already taken.
                                </p>
                                <p>
                                    On November 19, 1969, astronauts Charles Conrad and Alan Bean made a difficult manual landing of the Apollo 12 lunar module in the Moon's Ocean of Storms, just a few hundred feet from an unmanned probe, Surveyor 3, that had landed in a crater on April 19, 1967. Unrecognized at the time, this was an important moment in the history of science. Bean and Conrad were about to conduct the first archaeological studies on the Moon.
                                </p>
                                <p>
                                    After the obligatory planting of the American flag and some geological sampling, Conrad and Bean made their way to Surveyor 3. They observed that the probe had bounced after touchdown and carefully photographed the impressions made by its footpads. The whole spacecraft was covered in dust, perhaps kicked up by the landing.
                                </p>
                                <p>
                                    The astronaut-archaeologists carefully removed the probe's television camera, remote sampling arm, and pieces of tubing. They bagged and labelled these artefacts, and stowed them on board their lunar module. On their return to Earth, they passed them on to the Daveson Space Centre in Houston, Texas, and the Hughes Air and Space Corporation in El Segundo, California. There, scientists analysed the changes in these aerospace artefacts.
                                </p>
                                <p>
                                    One result of the analysis astonished them. A fragment of the television camera revealed evidence of the bacteria Streptococcus mitis. For a moment it was thought Conrad and Bean had discovered evidence for life on the Moon, but after further research the real explanation became apparent. While the camera was being installed in the probe prior to the launch, someone sneezed on it. The resulting bacteria had travelled to the Moon, remained in an alternating freezing, boiling vacuum for more than two years, and returned promptly to life upon reaching the safety of a laboratory back on Earth.
                                </p>
                                <p>
                                    The finding that not even the vastness of space can stop humans from spreading a sore throat was an unexpected spin-off. But the artefacts brought back by Bean and Conrad have a broader significance. Simple as they may seem, they provide the first example of extraterrestrial archaeology and perhaps more significant for the history of the discipline — formational archaeology, the study of environmental and cultural forces upon the life history of human artefacts in space.
                                </p>
                            </div>
                        </div>
                    </div>






                    {/* Right: Flowchart Questions */}
                    <div
                        className="w-[50%] flex flex-col items-start bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 h-full max-h-full overflow-y-auto relative"
                        style={{ overflowAnchor: 'none' }} // Optional: prevents jumping
                    >
                        {/* Questions Header */}
                        <div className="mb-6 w-full">
                            <h3 className="text-[15px] font-bold mb-3 text-left">Questions 34 – 38</h3>
                            <p className="text-[14px] mb-4 text-left">
                                Complete the flow chart below. <br />
                                Write <strong>ONE WORD ONLY</strong> from the passage for each answer.
                            </p>
                        </div>

                        {/* Flowchart */}
                        <div className="flex flex-col items-center text-[14px] text-black space-y-3 w-full">
                            {/* Step 1 */}
                            <div className="w-full bg-[#E3E9F0] text-center border border-[#A0A9B8] rounded-lg p-4 shadow-sm">
                                During the assembly of the Surveyor 3 probe, someone
                                <span
                                    className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-1 ml-2 mr-2 shadow"
                                    style={{ minWidth: 105, minHeight: 26 }}
                                >
                                    <input
                                        ref={el => questionRefs.current[33] = el}
                                        type="text"
                                        placeholder="34"
                                        className="w-full h-full bg-transparent text-center text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                        value={answers[33] || ""}
                                        onChange={e => {
                                            const newAnswers = [...answers];
                                            newAnswers[33] = e.target.value;
                                            setAnswers(newAnswers);
                                        }}
                                    />
                                </span>
                                on a TV camera.
                            </div>
                            <div className="text-[32px] font-bold text-gray-600 leading-none">↓</div>

                            {/* Step 2 */}
                            <div className="w-full bg-[#E3E9F0] text-center border border-[#A0A9B8] rounded-lg p-4 shadow-sm">
                                The TV Camera was carried to the Moon on Surveyor 3
                            </div>
                            <div className="text-[32px] font-bold text-gray-600 leading-none">↓</div>

                            {/* Step 3 */}
                            <div className="w-full bg-[#E3E9F0] text-center border border-[#A0A9B8] rounded-lg p-4 shadow-sm">
                                The TV Camera remained on the Moon for over
                                <span
                                    className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-1 ml-2 mr-2 shadow"
                                    style={{ minWidth: 105, minHeight: 26 }}
                                >
                                    <input
                                        ref={el => questionRefs.current[34] = el}
                                        type="text"
                                        placeholder="35"
                                        className="w-full h-full bg-transparent text-center text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                        value={answers[34] || ""}
                                        onChange={e => {
                                            const newAnswers = [...answers];
                                            newAnswers[34] = e.target.value;
                                            setAnswers(newAnswers);
                                        }}
                                    />
                                </span>
                                years
                            </div>
                            <div className="text-[32px] font-bold text-gray-600 leading-none">↓</div>

                            {/* Step 4 */}
                            <div className="w-full bg-[#E3E9F0] text-center border border-[#A0A9B8] rounded-lg p-4 shadow-sm">
                                Apollo 12 astronauts
                                <span
                                    className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-1 ml-2 mr-2 shadow"
                                    style={{ minWidth: 105, minHeight: 26 }}
                                >
                                    <input
                                        ref={el => questionRefs.current[35] = el}
                                        type="text"
                                        placeholder="36"
                                        className="w-full h-full bg-transparent text-center text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                        value={answers[35] || ""}
                                        onChange={e => {
                                            const newAnswers = [...answers];
                                            newAnswers[35] = e.target.value;
                                            setAnswers(newAnswers);
                                        }}
                                    />
                                </span>
                                the TV camera
                            </div>
                            <div className="text-[32px] font-bold text-gray-600 leading-none">↓</div>

                            {/* Step 5 */}
                            <div className="w-full bg-[#E3E9F0] text-center border border-[#A0A9B8] rounded-lg p-4 shadow-sm">
                                The TV camera was returned to Earth for
                                <span
                                    className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-1 ml-2 mr-2 shadow"
                                    style={{ minWidth: 105, minHeight: 26 }}
                                >
                                    <input
                                        ref={el => questionRefs.current[36] = el}
                                        type="text"
                                        placeholder="37"
                                        className="w-full h-full bg-transparent text-center text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                        value={answers[36] || ""}
                                        onChange={e => {
                                            const newAnswers = [...answers];
                                            newAnswers[36] = e.target.value;
                                            setAnswers(newAnswers);
                                        }}
                                    />
                                </span>
                            </div>
                            <div className="text-[32px] font-bold text-gray-600 leading-none">↓</div>

                            {/* Step 6 */}
                            <div className="w-full bg-[#E3E9F0] text-center border border-[#A0A9B8] rounded-lg p-4 shadow-sm">
                                The theory that this suggested there was
                                <span
                                    className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-1 ml-2 mr-2 shadow"
                                    style={{ minWidth: 105, minHeight: 26 }}
                                >
                                    <input
                                        ref={el => questionRefs.current[37] = el}
                                        type="text"
                                        placeholder="38"
                                        className="w-full h-full bg-transparent text-center text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                        value={answers[37] || ""}
                                        onChange={e => {
                                            const newAnswers = [...answers];
                                            newAnswers[37] = e.target.value;
                                            setAnswers(newAnswers);
                                        }}
                                    />
                                </span>
                                on the Moon was rejected.
                            </div>
                        </div>
                    </div>








                </div>
            </div>




            {/* Main content (scrollable) */}
            <div className="flex-1 flex flex-col items-center justify-start w-full">
                {/* Two-Column Main Content Area */}
                <div ref={highlightRef}
                    className="flex flex-row w-full overflow-hidden -mb-16 mt-2 px-12 pb-20"
                    style={{ height: 'calc(100vh - 180px)' }}
                >
                    {/* Left: Reading Passage */}
                    <div className="bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 w-[49%] h-full max-h-full overflow-y-auto">
                        <div className="mb-8">
                            <h2 className="text-[16px] font-bold mb-4 text-center">Early Telecommunication Devices</h2>
                            <h3 className="text-[14px] italic mb-6 text-center">
                                Although it is hardly used anymore, the telegraph is familiar to most people. This early telecommunication devices are credited, as any school student knows, to Samuel Morse, who, in 1844 made the first long-distance electronic communication via his invention, the Morse's telegraph. What is not so commonly known is that Morse's was not the only telegraph nor he the only such inventor at this time. A rival system, developed by William Cook and Charles Wheatstone, was patented in England in 1845 and was subsequently adopted for use by British rail companies to enable speedy communication between rail stations.
                            </h3>

                            <div className="text-[14px] space-y-4 text-left">
                                <p>
                                    However, the Cooke-Wheatstone telegraph, which used six wires and a fragile receiver requiring five magnetic needles, proved to the awkward to use, difficult to transport and expensive to build. Morse's version used one wire and a receiver of a simpler and stronger design. This is, no doubt, why it became the favoured telegraph in many parts of the world, especially the United States, which built a telegraph line along railway tracks crossing the North American continent, linking eastern cities with western frontiers.
                                </p>
                                <p>
                                    Morse chose the Magnetic Telegraph Company to handle the patents for his telegraph technology, and within seven years of the appearance of his invention, the company had licensed use of the telegraph to more than 50 companies across the US. In 1851, twelve of this companies come together to form the Western Union Company. By 1866, Western Union had grown to include more than 4000 telegraph offices, almost all in rail stations.
                                </p>
                                <p>
                                    Another early telecommunications device is still very much with us the telephone. Although the telephone is popularly thought to be the brainchild of one man, Alexander Graham Bell, this is not the whole truth. Phillip Reis, a schoolteacher in Germany, invented a device in 1861 that he labelled a telephone. Reis's invention was limited to transmitting musical tones, however, and could not send the sound of the human voice across the wire.
                                </p>
                                <p>
                                    While Reis was working on his invention, Bell and another man, Elisha Gray, were also working toward the invention of the telephone, though by an indirect route. Both were, in fact, seeking ways of allowing multiple telegraph signals to travel along some telegraph line - a system known as a harmonic telegraph. Bell worked in Boston while Gray was based in Chicago, and the tow were rivals in their area of research. for both inventors, the perfection of the harmonic telegraph proved too difficult and both, separately but at around the sometime, changed plans and started on the development of a telephone. Most interesting of all is the fact that both men applied for a patent to the US patent office for their respective telephones on the same day, 14 February 1876. Bell was lucky enough to have arrived a few hours earlier than Gray and so it was Bell whose name was to be forever associated with the telephone. The harmonic telegraph, incidentally, was perfected by Thomas Edison, best known as the inventor of the light built, in 1881.
                                </p>
                                <p>
                                    Rights to Bell's patent (now recognized as the most valuable patent in history of technology) were offered to Western Union for $100000, with the assumption that the giant telegraph company would be enthusiastic about the new technology. But Western Union disliked Bell's design and instead asked Elisha Gray to make refinements to his original telephone design. Bell`s company began to set up its own business and sell telephones, while Western Union, with its somewhat different design, was its competitor.
                                </p>
                                <p>
                                    Competition between the two continued for about two years, but all the while, the Bell company was mounting a legal challenge to Western Union, claiming it held the only true basic patents for the telephone. it based its claim on the fact that Bell had beaten Gray to the patent office and so should be the sole recognized inventor of the telephone. Eventually, Western Union had to agree with Bell and gave up its telephone rights and patents to the Bell company. The telephone company's entire network of telephones was handed over to the Bell company. As compensation, Western Union was given 20 per cent of revenue from rental of its former equipment, this arrangement was to last until Bell's patents expired. In an effort to fight the power the Bell company enjoyed from exclusive rights to Bell's patents, a small telephone company, Pacific Union, established telephone services in the 1920s and 1930s that it claimed were based on the telephone design of Phillips Reis. They maintained that because Reis's invention pre-dated Bell1s, the Bell, design was not the first of its kind and, therefore, Bell's patents were not valid. Although the court accepted that the company may have been using Reis's technology, it nonetheless held that only Bell's patents could legally by used.
                                </p>
                                <p>
                                    The Bell company, eventually named American Telephone & Telegraph, thus formed an effective monopoly on telephone services in the United States. The company subsequently grew to such an extent that, a century later, it was the largest privately held enterprise in the world, with more than a million employees controlling communication between more than 100 million telephones. In 1984, American Telephone & Telegraph was found by a US court to be too monopolistic and was ordered to be broken up into several smaller companies.
                                </p>
                                <p>
                                    <strong>* patent:</strong> an official recognition of a person as the inventor of a device<br />
                                    <strong>* monopoly:</strong> exclusive control of a market
                                </p>
                            </div>
                        </div>
                    </div>






                    {/* Right: Questions */}
                    <div
                        className="w-[50%] flex flex-col items-start bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 h-full max-h-full overflow-y-auto relative"
                        style={{ overflowAnchor: 'none' }}
                    >
                        {/* Questions Header */}
                        <div className="mb-6 w-full">
                            <h3 className="text-[15px] font-bold mb-3 text-left">Questions 26 – 29</h3>
                            <p className="text-[14px] mb-4 text-left">
                                Write <strong>NO MORE THAN THREE WORDS</strong> from the text in each gap.
                            </p>
                        </div>

                        {/* Questions List */}
                        <div className="space-y-5 w-full text-[14px] text-black">
                            {/* Question 26 */}
                            <div>
                                <div className="flex items-start gap-x-2">
                                    <span className="font-semibold">26)</span>
                                    <p className="flex-1">
                                        Name one reason why Cooke and Wheatstone's invention was not as successful as Morse'?
                                    </p>
                                </div>
                                <div
                                    className="mt-2 rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[60%]"
                                    style={{ minWidth: 180, maxWidth: 300 }}
                                >
                                    <input
                                        ref={el => questionRefs.current[25] = el}
                                        type="text"
                                        placeholder="26"
                                        className="w-full bg-transparent text-center text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                        value={answers[25] || ""}
                                        onChange={e => {
                                            const newAnswers = [...answers];
                                            newAnswers[25] = e.target.value;
                                            setAnswers(newAnswers);
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Question 27 */}
                            <div>
                                <div className="flex items-start gap-x-2">
                                    <span className="font-semibold">27)</span>
                                    <p className="flex-1">
                                        In what type of location did Western Union typically offer its telegraph services?
                                    </p>
                                </div>
                                <div
                                    className="mt-2 rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[60%]"
                                    style={{ minWidth: 180, maxWidth: 300 }}
                                >
                                    <input
                                        ref={el => questionRefs.current[26] = el}
                                        type="text"
                                        placeholder="27"
                                        className="w-full bg-transparent text-center text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                        value={answers[26] || ""}
                                        onChange={e => {
                                            const newAnswers = [...answers];
                                            newAnswers[26] = e.target.value;
                                            setAnswers(newAnswers);
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Question 28 */}
                            <div>
                                <div className="flex items-start gap-x-2">
                                    <span className="font-semibold">28)</span>
                                    <p className="flex-1">
                                        What sort of information was Reis's original invention able to send?
                                    </p>
                                </div>
                                <div
                                    className="mt-2 rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[60%]"
                                    style={{ minWidth: 180, maxWidth: 300 }}
                                >
                                    <input
                                        ref={el => questionRefs.current[27] = el}
                                        type="text"
                                        placeholder="28"
                                        className="w-full bg-transparent text-center text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                        value={answers[27] || ""}
                                        onChange={e => {
                                            const newAnswers = [...answers];
                                            newAnswers[27] = e.target.value;
                                            setAnswers(newAnswers);
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Question 29 */}
                            <div>
                                <div className="flex items-start gap-x-2">
                                    <span className="font-semibold">29)</span>
                                    <p className="flex-1">
                                        What device did Alexander Graham Bell try but fail to invent?
                                    </p>
                                </div>
                                <div
                                    className="mt-2 rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[60%]"
                                    style={{ minWidth: 180, maxWidth: 300 }}
                                >
                                    <input
                                        ref={el => questionRefs.current[28] = el}
                                        type="text"
                                        placeholder="29"
                                        className="w-full bg-transparent text-center text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                        value={answers[28] || ""}
                                        onChange={e => {
                                            const newAnswers = [...answers];
                                            newAnswers[28] = e.target.value;
                                            setAnswers(newAnswers);
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>



            {/* Main content (scrollable) */}
            <div className="flex-1 flex flex-col items-center justify-start w-full">
                {/* Two-Column Main Content Area */}
                <div ref={highlightRef}
                    className="flex flex-row w-full overflow-hidden -mb-16 mt-2 px-12 pb-20"
                    style={{ height: 'calc(100vh - 180px)' }}
                >
                    {/* Left: Reading Passage */}
                    <div className="bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 w-[49%] h-full max-h-full overflow-y-auto">
                        <div className="mb-8">
                            <h2 className="text-[16px] font-bold mb-4 text-center">Tidal Power</h2>
                            <h3 className="text-[14px] font-semibold mb-6 text-center italic">
                                Undersea turbines which produce electricity from the tides are set to become an important source of renewable energy for Britain. It is still too early to predict the extent of the impact they may have, but all the signs are that they will play a significant role in the future.
                            </h3>

                            {/* Remaining paragraphs - left aligned */}
                            <div className="text-[14px] space-y-4 text-left">
                                <p>
                                    <strong>A</strong> Operating on the same principle as wind turbines, the power in sea turbines comes from tidal currents which turn blades similar to ships' propellers, but, unlike wind, the tides are predictable and the power input is constant. The technology raises the prospect of Britain becoming self-sufficient in renewable energy and drastically reducing its carbon dioxide emissions. If tide, wind and wave power are all developed, Britain would be able to close gas, coal and nuclear power plants and export renewable power to other parts of Europe. Unlike wind power, which Britain originally developed and then abandoned for 20 years allowing the Dutch to make it a major industry, undersea turbines could become a big export earner to island nations such as Japan and New Zealand.
                                </p>
                                <p>
                                    <strong>B</strong> Tidal sites have already been identified that will produce one sixth or more of the UK's power - and at prices competitive with modern gas turbines and undercutting those of the already ailing nuclear industry. One site alone, the Pentland Firth, between Orkney and mainland Scotland, could produce 10% of the country's electricity with banks of turbines under the sea, and another at Alderney in the Channel Islands three times the 1,200 megawatts of Britain's largest and newest nuclear plant, Sizewell B, in Suffolk. Other sites identified include the Bristol Channel and the west coast of Scotland, particularly the channel between Campbeltown and Northern Ireland.
                                </p>
                                <p>
                                    <strong>C</strong> Work on designs for the new turbine blades and sites are well advanced at the University of Southampton's sustainable energy research group. The first station is expected to be installed off Lynmouth in Devon shortly to test the technology in a venture jointly funded by the department of Trade and Industry and the European Union. Abu-Bakr Bahaj, in charge of the Southampton research, said: The prospects for energy from tidal currents are far better than from wind because the flows of water are predictable and constant. The technology for dealing with the hostile saline environment under the sea has been developed in the North Sea oil industry and much is already known about turbine blade design, because of wind power and ship propellers. There are a few technical difficulties, but I believe in the next five to ten years we will be installing commercial marine turbine farms. ' Southampton has been awarded £215,000 over three years to develop the turbines and is working with Marine Current Turbines, a subsidiary of IT power, on the Lynmouth project. EU research has now identified 106 potential sites for tidal power, 80% round the coasts of Britain. The best sites are between islands or around heavily indented coasts where there are strong tidal currents.
                                </p>
                                <p>
                                    <strong>D</strong> A marine turbine blade needs to be only one third of the size of a wind generator to produce three times as much power. The blades will be about 20 metres in diameter, so around 30 metres of water is required. Unlike wind power, there are unlikely to be environmental objections. Fish and other creatures are thought unlikely to be at risk from the relatively slow-turning blades. Each turbine will be mounted on a tower which will connect to the national power supply grid via underwater cables. The towers will stick out of the water and be lit, to warn shipping, and also be designed to be lifted out of the water for maintenance and to clean seaweed from the blades.
                                </p>
                                <p>
                                    <strong>E</strong> Dr Bahaj has done most work on the Alderney site, where there are powerful currents. The single undersea turbine farm would produce far more power than needed for the Channel Islands and most would be fed into the French Grid and be re-imported into Britain via the cable under the Channel.
                                </p>
                                <p>
                                    <strong>F</strong> One technical difficulty is cavitation, where low pressure behind a turning blade causes air bubbles. These can cause vibration and damage the blades of the turbines. Dr Bahaj said: 'We have to test a number of blade types to avoid this happening or at least make sure it does not damage the turbines or reduce performance. Another slight concern is submerged debris floating into the blades. So far, we do not know how much of a problem it might be. We will have to make the turbines robust because the sea is a hostile environment, but all the signs that we can do it are good.'
                                </p>
                            </div>
                        </div>
                    </div>





                    {/* Right: Questions */}
                    <div
                        className="w-[50%] flex flex-col items-start bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 h-full max-h-full overflow-y-auto relative"
                        style={{ overflowAnchor: 'none' }} // prevents scroll jumping
                    >
                        {/* Questions Header */}
                        <div className="mb-6 w-full">
                            <h3 className="text-[15px] font-bold mb-3 text-left">
                                Questions 23 – 26
                            </h3>
                            <p className="text-[14px] mb-4 text-left">
                                Choose <strong>NO MORE THAN TWO WORDS</strong> from the passage for each answer.
                            </p>
                        </div>

                        {/* Questions List */}
                        <div className="space-y-4 w-full">
                            {[23, 24, 25, 26].map((num) => (
                                <div key={num} className="flex items-center w-full">
                                    {/* Question Number */}
                                    <div className="text-[14px] text-black font-medium mr-3 min-w-[24px]">
                                        {num})
                                    </div>

                                    {/* Input Box */}
                                    <div
                                        className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[250px] max-w-full"
                                        style={{ minHeight: 26 }}
                                    >
                                        <input
                                            ref={(el) => (questionRefs.current[num - 1] = el)}
                                            type="text"
                                            placeholder={`${num}`}
                                            className="w-full h-full bg-transparent text-left text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                            value={answers[num - 1] || ""}
                                            onChange={(e) => {
                                                const newAnswers = [...answers];
                                                newAnswers[num - 1] = e.target.value;
                                                setAnswers(newAnswers);
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* Image Below Questions */}
                        <div className="mt-8 w-full flex justify-center">
                            <img
                                src={showgunImg}
                                alt="Shogun illustration"
                                className="rounded shadow-md max-w-full"
                            />
                        </div>
                    </div>
                </div>
            </div>



            {/* Main content (scrollable) */}
            <div className="flex-1 flex flex-col items-center justify-start w-full">
                {/* Two-Column Main Content Area */}
                <div ref={highlightRef}
                    className="flex flex-row w-full overflow-hidden -mb-16 mt-2 px-12 pb-20"
                    style={{ height: 'calc(100vh - 180px)' }}
                >
                    {/* Left: Reading Passage */}
                    <div className="bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 w-[49%] h-full max-h-full overflow-y-auto">
                        <div className="mb-8">
                            <h2 className="text-[16px] font-bold mb-4 text-center">Urban farming</h2>
                            <h3 className="text-[14px] font-semibold mb-6 text-center italic">
                                In Paris, urban farmers are trying a soil-free approach to agriculture that uses less space and fewer resources. Could it help cities face the threats to our food supplies?
                            </h3>

                            {/* Remaining paragraphs - left aligned */}
                            <div className="text-[14px] space-y-4 text-left">
                                <p>
                                    On top of a striking new exhibition hall in southern Paris, the world's largest urban rooftop farm has started to bear fruit. Strawberries that are small, intensely flavoured and resplendently red sprout abundantly from large plastic tubes. Peer inside and you see the tubes are completely hollow, the roots of dozens of strawberry plants dangling down inside them. From identical vertical tubes nearby burst row upon row of lettuces; near those are aromatic herbs, such as basil, sage and peppermint. Opposite, in narrow, horizontal trays packed not with soil but with coconut fibre, grow cherry tomatoes, shiny aubergines and brightly coloured chards.
                                </p>
                                <p>
                                    Pascal Hardy, an engineer and sustainable development consultant, began experimenting with vertical farming and aeroponic growing towers - as the soil-free plastic tubes are known – on his Paris apartment block roof five years ago. The urban rooftop space above the exhibition hall is somewhat bigger: 14,000 square metres and almost exactly the size of a couple of football pitches. Already, the team of young urban farmers who tend it have picked, in one day, 3,000 lettuces and 150 punnets of strawberries. When the remaining two thirds of the vast open area are in production, 20 staff will harvest up to 1,000 kg of perhaps 35 different varieties of fruit and vegetables, every day. 'We're not ever, obviously, going to feed the whole city this way,' cautions Hardy. 'In the urban environment you're working with very significant practical constraints, clearly, on what you can do and where. But if enough unused space can be developed like this, there's no reason why you shouldn't eventually target maybe between 5% and 10% of consumption.'
                                </p>
                                <p>
                                    Perhaps most significantly, however, this is a real-life showcase for the work of Hardy's flourishing urban agriculture consultancy, Agripolis, which is currently fielding enquiries from around the world to design, build and equip a new breed of soil-free inner-city farm. 'The method's advantages are many,' he says. 'First, I don't much like the fact that most of the fruit and vegetables we eat have been treated with something like 17 different pesticides, or that the intensive farming techniques that produced them are such huge generators of greenhouse gases. I don't much like the fact, either, that they've travelled an average of 2,000 refrigerated kilometres to my plate, that their quality is so poor, because the varieties are selected for their capacity to withstand such substantial journeys, or that 80% of the price I pay goes to wholesalers and transport companies, not the producers.'
                                </p>
                                <p>
                                    Produce grown using this soil-free method, on the other hand – which relies solely on a small quantity of water, enriched with organic nutrients, pumped around a closed circuit of pipes, towers and trays – is 'produced up here, and sold locally, just down there,' Hardy says. 'You can select crop varieties for their flavour, not their resistance to the transport and storage chain, and you can pick them when they're really at their best, and not before.' No soil is exhausted, and the water that gently showers the plants' roots every 12 minutes is recycled, so the method uses 90% less water than a classic intensive farm for the same yield.
                                </p>
                                <p>
                                    Urban farming is not, of course, a new phenomenon. Inner-city agriculture is booming from Shanghai to Detroit and Tokyo to Bangkok. Strawberries are being grown in disused shipping containers, mushrooms in underground carparks. Aeroponic farming, he says, is 'virtuous'. The equipment weighs little, can be installed on almost any flat surface and is cheap to buy: roughly 100 to 150 per square metre. It is cheap to run, too, consuming a tiny fraction of the electricity used by some techniques.
                                </p>
                                <p>
                                    Produce grown this way typically sells at prices that, while generally higher than those of classic intensive agriculture, are lower than soil-based organic growers. There are limits to what farmers can grow this way, of course, and much of the produce is suited to the summer months. 'Root vegetables we cannot do, at least not yet,' he says. 'Radishes are OK, but carrots, potatoes, that kind of thing – the roots are simply too long. Fruit trees are obviously not an option. And beans tend to take up a lot of space for not much return.' Nevertheless, urban farming of the kind being practised in Paris is one part of a bigger and fast-changing picture that is bringing food production closer to our lives.
                                </p>
                            </div>
                        </div>
                    </div>





                    {/* Right: Questions */}
                    <div
                        className="w-[50%] flex flex-col items-start bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 h-full max-h-full overflow-y-auto relative"
                        style={{ overflowAnchor: 'none' }}
                    >
                        {/* Questions Header */}
                        <div className="mb-6 w-full">
                            <h3 className="text-[15px] font-bold mb-3 text-left">
                                Questions 4 – 7
                            </h3>
                            <p className="text-[14px] mb-4 text-left">
                                Complete the table below.<br />
                                Choose <strong>ONE WORD ONLY</strong> from the passage for each answer.
                            </p>
                        </div>

                        {/* Table */}
                        <table className="w-full border border-gray-300 text-[14px] text-left">
                            <thead>
                                <tr className="bg-gray-100 border-b border-gray-300">
                                    <th className="p-2 border-r border-gray-300 w-[35%]"> </th>
                                    <th className="p-2 border-r border-gray-300 w-[20%]">Growth</th>
                                    <th className="p-2 border-r border-gray-300 w-[20%]">Selection</th>
                                    <th className="p-2 border-gray-300 w-[25%]">Sale</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Row: Intensive farming */}
                                <tr className="border-t border-gray-300 align-top">
                                    <td className="p-2 border-r border-gray-300 font-semibold">Intensive farming</td>
                                    <td className="p-2 border-r border-gray-300">
                                        <div>
                                            ● wide range of&nbsp;
                                            <input
                                                ref={(el) => (questionRefs.current[3] = el)}
                                                type="text"
                                                placeholder="4"
                                                className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-0.5 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                                value={answers[3] || ""}
                                                onChange={(e) => {
                                                    const newAnswers = [...answers];
                                                    newAnswers[3] = e.target.value;
                                                    setAnswers(newAnswers);
                                                }}
                                            />
                                            &nbsp;used
                                        </div>
                                        <div>● techniques pollute air</div>
                                    </td>
                                    <td className="p-2 border-r border-gray-300">
                                        ● quality not good
                                        <br />
                                        ● varieties of fruit and vegetables chosen that can survive long&nbsp;
                                        <input
                                            ref={(el) => (questionRefs.current[4] = el)}
                                            type="text"
                                            placeholder="5"
                                            className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-0.5 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                            value={answers[4] || ""}
                                            onChange={(e) => {
                                                const newAnswers = [...answers];
                                                newAnswers[4] = e.target.value;
                                                setAnswers(newAnswers);
                                            }}
                                        />
                                    </td>
                                    <td className="p-2">
                                        ●&nbsp;
                                        <input
                                            ref={(el) => (questionRefs.current[5] = el)}
                                            type="text"
                                            placeholder="6"
                                            className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-0.5 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                            value={answers[5] || ""}
                                            onChange={(e) => {
                                                const newAnswers = [...answers];
                                                newAnswers[5] = e.target.value;
                                                setAnswers(newAnswers);
                                            }}
                                        />
                                        &nbsp;receive very little of overall income
                                    </td>
                                </tr>

                                {/* Row: Aeroponic urban farming */}
                                <tr className="border-t border-gray-300 align-top">
                                    <td className="p-2 border-r border-gray-300 font-semibold">Aeroponic urban farming</td>
                                    <td className="p-2 border-r border-gray-300">
                                        ● no soil used
                                        <br />
                                        ● nutrients added to water, which is recycled
                                    </td>
                                    <td className="p-2 border-r border-gray-300">
                                        ● produce chosen because of its&nbsp;
                                        <input
                                            ref={(el) => (questionRefs.current[6] = el)}
                                            type="text"
                                            placeholder="7"
                                            className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-0.5 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                            value={answers[6] || ""}
                                            onChange={(e) => {
                                                const newAnswers = [...answers];
                                                newAnswers[6] = e.target.value;
                                                setAnswers(newAnswers);
                                            }}
                                        />
                                    </td>
                                    <td className="p-2"></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>


            {/* Main content (scrollable) */}
            <div className="flex-1 flex flex-col items-center justify-start w-full">
                {/* Two-Column Main Content Area */}
                <div ref={highlightRef}
                    className="flex flex-row w-full overflow-hidden -mb-16 mt-2 px-12 pb-20"
                    style={{ height: 'calc(100vh - 180px)' }}
                >
                    {/* Left: Reading Passage */}
                    <div className="bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 w-[49%] h-full max-h-full overflow-y-auto">
                        <div className="mb-8">
                            <h2 className="text-[16px] font-bold mb-4 text-center">Stonehenge</h2>
                            <h3 className="text-[14px] font-semibold mb-6 text-center italic">
                                For centuries, historians and archaeologists have puzzled over the many mysteries of Stonehenge, a prehistoric monument that took an estimated 1,500 years to erect.
                            </h3>

                            {/* Remaining paragraphs - left aligned */}
                            <div className="text-[14px] space-y-4 text-left">
                                <p>
                                    Located on Salisbury Plain in southern England, it is comprised of roughly 100 massive upright stones placed in a circular layout.
                                </p>
                                <p>
                                    Archaeologists believe England's most iconic prehistoric ruin was built in several stages with the earliest constructed 5,000 or more years ago. First, Neolithic* Britons used primitive tools, which may have been fashioned out of deer antlers, to dig a massive circular ditch and bank, or henge. Deep pits dating back to that era and located within the circle may have once held a ring of timber posts, according to some scholars.
                                </p>
                                <p>
                                    Several hundred years later, it is thought, Stonehenge's builders hoisted an estimated 80 bluestones, 43 of which remain today, into standing positions and placed them in either a horseshoe or circular formation. These stones have been traced all the way to the Preseli Hills in Wales, some 300 kilometres from Stonehenge. How, then, did prehistoric builders without sophisticated tools or engineering haul these boulders, which weigh up to four tons, over such a great distance?
                                </p>
                                <p>
                                    According to one long-standing theory among archaeologists, Stonehenge's builders fashioned sledges and rollers out of tree trunks to lug the bluestones from the Preseli Hills. They then transferred the boulders onto rafts and floated them first along the Welsh coast and then up the River Avon toward Salisbury Plain; alternatively, they may have towed each stone with a fleet of vessels. More recent archaeological hypotheses have them transporting the bluestones with supersized wicker baskets on a combination of ball bearings and long grooved planks, hauled by oxen.
                                </p>
                                <p>
                                    As early as the 1970s, geologists have been adding their voices to the debate over how Stonehenge came into being. Challenging the classic image of industrious builders pushing, carting, rolling or hauling giant stones from faraway Wales, some scientists have suggested that it was glaciers, not humans, that carried the bluestones to Salisbury Plain. Most archaeologists have remained sceptical about this theory, however, wondering how the forces of nature could possibly have delivered the exact number of stones needed to complete the circle.
                                </p>
                                <p>
                                    The third phase of construction took place around 2000 BCE. At this point, sandstone slabs – known as 'sarsens' – were arranged into an outer crescent or ring; some were assembled into the iconic three-pieced structures called trilithons that stand tall in the centre of Stonehenge. Some 50 of these stones are now visible on the site, which may once have contained many more. Radiocarbon dating has revealed that work continued at Stonehenge until roughly 1600 BCE, with the bluestones in particular being repositioned multiple times.
                                </p>
                                <p>
                                    But who were the builders of Stonehenge? In the 17th century, archaeologist John Aubrey made the claim that Stonehenge was the work of druids, who had important religious, judicial and political roles in Celtic** society. This theory was widely popularized by the antiquarian William Stukeley, who had unearthed primitive graves at the site. Even today, people who identify as modern druids continue to gather at Stonehenge for the summer solstice. However, in the mid-20th century, radiocarbon dating demonstrated that Stonehenge stood more than 1,000 years before the Celts inhabited the region.
                                </p>
                                <p>
                                    Many modern historians and archaeologists now agree that several distinct tribes of people contributed to Stonehenge, each undertaking a different phase of its construction. Bones, tools and other artefacts found on the site seem to support this hypothesis. The first stage was achieved by Neolithic agrarians who were likely to have been indigenous to the British Isles. Later, it is believed, groups with advanced tools and a more communal way of life left their mark on the site. Some believe that they were immigrants from the European continent, while others maintain that they were probably native Britons, descended from the original builders.
                                </p>
                                <p>
                                    If the facts surrounding the architects and construction of Stonehenge remain shadowy at best, the purpose of the striking monument is even more of a mystery. While there is consensus among the majority of modern scholars that Stonehenge once served the function of burial ground, they have yet to determine what other purposes it had.
                                </p>
                                <p>
                                    In the 1960s, the astronomer Gerald Hawkins suggested that the cluster of megalithic stones operated as a form of calendar, with different points corresponding to astrological phenomena such as solstices, equinoxes and eclipses occurring at different times of the year. While his theory has received a considerable amount of attention over the decades, critics maintain that Stonehenge's builders probably lacked the knowledge necessary to predict such events or that England's dense cloud cover would have obscured their view of the skies.
                                </p>
                                <p>
                                    More recently, signs of illness and injury in the human remains unearthed at Stonehenge led a group of British archaeologists to speculate that it was considered a place of healing, perhaps because bluestones were thought to have curative powers.
                                </p>
                            </div>
                        </div>
                    </div>





                    {/* Right: Questions */}
                    <div
                        className="w-[50%] flex flex-col items-start bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 h-full max-h-full overflow-y-auto relative"
                        style={{ overflowAnchor: 'none' }}
                    >
                        {/* Questions 1 – 8: Notes */}
                        <div className="w-full">
                            <h3 className="text-[15px] font-bold mb-3 text-left">Questions 1 – 8</h3>
                            <p className="text-[14px] mb-2 text-left italic">
                                Complete the notes below.
                            </p>
                            <p className="text-[14px] mb-4 text-left">
                                Choose <strong>NO MORE THAN TWO WORDS</strong> from the passage for each answer.
                            </p>

                            <h2 className="text-[16px] font-bold text-center mb-2">Stonehenge</h2>

                            <h3 className="text-[15px] font-bold mb-1 mt-4 text-left">Construction</h3>
                            <div className="text-[14px] font-medium text-blac text-leftk">
                                <div className="ml-2 mb-2 text-left">
                                    <span className="font-semibold text-left">Stage 1:</span>
                                    <ul className="list-disc list-inside ml-5 mt-1">
                                        <li>
                                            the ditch and henge were dug, possibly using tools made from&nbsp;
                                            <input
                                                ref={(el) => (questionRefs.current[0] = el)}
                                                type="text"
                                                placeholder="1"
                                                className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                                value={answers[0] || ""}
                                                onChange={(e) => {
                                                    const newAnswers = [...answers];
                                                    newAnswers[0] = e.target.value;
                                                    setAnswers(newAnswers);
                                                }}
                                            />
                                            .
                                        </li>
                                        <li>
                                            <input
                                                ref={(el) => (questionRefs.current[1] = el)}
                                                type="text"
                                                placeholder="2"
                                                className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                                value={answers[1] || ""}
                                                onChange={(e) => {
                                                    const newAnswers = [...answers];
                                                    newAnswers[1] = e.target.value;
                                                    setAnswers(newAnswers);
                                                }}
                                            />
                                            &nbsp;may have been arranged in deep pits inside the circle
                                        </li>
                                    </ul>
                                </div>
                                <div className="ml-2 mb-2 text-left">
                                    <span className="font-semibold text-left">Stage 2:</span>
                                    <ul className="list-disc list-inside ml-5 mt-1">
                                        <li>bluestones from the Preseli Hills were placed in standing position</li>
                                        <li>
                                            theories about the transportation of the bluestones:
                                            <ul className="list-none ml-5 mt-1">
                                                <li>
                                                    <span className="font-semibold">archaeological:</span>
                                                    <ul className="list-disc list-inside ml-5 mt-1">
                                                        <li>
                                                            builders used&nbsp;
                                                            <input
                                                                ref={(el) => (questionRefs.current[2] = el)}
                                                                type="text"
                                                                placeholder="3"
                                                                className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                                                value={answers[2] || ""}
                                                                onChange={(e) => {
                                                                    const newAnswers = [...answers];
                                                                    newAnswers[2] = e.target.value;
                                                                    setAnswers(newAnswers);
                                                                }}
                                                            />
                                                            &nbsp;to make sledges and rollers
                                                        </li>
                                                        <li>
                                                            <input
                                                                ref={(el) => (questionRefs.current[3] = el)}
                                                                type="text"
                                                                placeholder="4"
                                                                className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                                                value={answers[3] || ""}
                                                                onChange={(e) => {
                                                                    const newAnswers = [...answers];
                                                                    newAnswers[3] = e.target.value;
                                                                    setAnswers(newAnswers);
                                                                }}
                                                            />
                                                            &nbsp;pulled them on giant baskets
                                                        </li>
                                                    </ul>
                                                </li>
                                                <li>
                                                    <span className="font-semibold">geological:</span>
                                                    <ul className="list-disc list-inside ml-5 mt-1">
                                                        <li>
                                                            they were brought from Wales by&nbsp;
                                                            <input
                                                                ref={(el) => (questionRefs.current[4] = el)}
                                                                type="text"
                                                                placeholder="5"
                                                                className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                                                value={answers[4] || ""}
                                                                onChange={(e) => {
                                                                    const newAnswers = [...answers];
                                                                    newAnswers[4] = e.target.value;
                                                                    setAnswers(newAnswers);
                                                                }}
                                                            />
                                                            .
                                                        </li>
                                                    </ul>
                                                </li>
                                            </ul>
                                        </li>
                                    </ul>
                                </div>
                                <div className="ml-2 mb-2 text-left">
                                    <span className="font-semibold text-left">Stage 3:</span>
                                    <ul className="list-disc list-inside ml-5 mt-1">
                                        <li>sandstone slabs were arranged into an outer crescent or ring</li>
                                    </ul>
                                </div>
                                <h3 className="text-[15px] font-bold mb-1 mt-4 text-left">Builders</h3>
                                <ul className="list-disc list-inside ml-5 mt-1">
                                    <li>
                                        a theory arose in the 17th century that its builders were Celtic&nbsp;
                                        <input
                                            ref={(el) => (questionRefs.current[5] = el)}
                                            type="text"
                                            placeholder="6"
                                            className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                            value={answers[5] || ""}
                                            onChange={(e) => {
                                                const newAnswers = [...answers];
                                                newAnswers[5] = e.target.value;
                                                setAnswers(newAnswers);
                                            }}
                                        />
                                        .
                                    </li>
                                </ul>
                                <h3 className="text-[15px] font-bold mb-1 mt-4 text-left">Purpose</h3>
                                <ul className="list-disc list-inside ml-5 mt-1">
                                    <li>
                                        many experts agree it has been used as a&nbsp;
                                        <input
                                            ref={(el) => (questionRefs.current[6] = el)}
                                            type="text"
                                            placeholder="7"
                                            className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                            value={answers[6] || ""}
                                            onChange={(e) => {
                                                const newAnswers = [...answers];
                                                newAnswers[6] = e.target.value;
                                                setAnswers(newAnswers);
                                            }}
                                        />
                                        &nbsp;site
                                    </li>
                                    <li>
                                        in the 1960s, it was suggested that it worked as a kind of&nbsp;
                                        <input
                                            ref={(el) => (questionRefs.current[7] = el)}
                                            type="text"
                                            placeholder="8"
                                            className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                            value={answers[7] || ""}
                                            onChange={(e) => {
                                                const newAnswers = [...answers];
                                                newAnswers[7] = e.target.value;
                                                setAnswers(newAnswers);
                                            }}
                                        />
                                        .
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>




            {/* Main content (scrollable) */}
            <div className="flex-1 flex flex-col items-center justify-start w-full">
                <div ref={highlightRef}
                    className="flex flex-row w-full overflow-hidden -mb-16 mt-2 px-12 pb-20"
                    style={{ height: 'calc(100vh - 180px)' }}
                >
                    {/* Left: Reading Passage */}
                    <div className="bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 w-[49%] h-full max-h-full overflow-y-auto">
                        <div className="mb-8">
                            <h2 className="text-[16px] font-bold mb-4 text-center">Conquering Earth's space junk problem</h2>

                            {/* First paragraph - centered, italic */}
                            <p className="text-[14px] italic font-semibold mb-4 text-center">
                                Satellites, rocket shards and collision debris are creating major traffic risks in orbit around the planet. Researchers are working to reduce these threats
                            </p>

                            {/* Remaining paragraphs - left aligned */}
                            <div className="text-[14px] space-y-4 text-left">
                                <p>
                                    Last year, commercial companies, military and civil departments and amateurs sent more than 400 satellites into orbit, over four times the yearly average in the previous decade. Numbers could rise even more sharply if leading space companies follow through on plans to deploy hundreds to thousands of large constellations of satellites to space in the next few years.
                                </p>
                                <p>
                                    All that traffic can lead to disaster. Ten years ago, a US commercial Iridium satellite smashed into an inactive Russian communications satellite called Cosmos-2251, creating thousands of new pieces of space shrapnel that now threaten other satellites in low Earth orbit – the zone stretching up to 2,000 kilometres in altitude. Altogether, there are roughly 20,000 human-made objects in orbit, from working satellites to small rocket pieces. And satellite operators can't steer away from every potential crash, because each move consumes time and fuel that could otherwise be used for the spacecraft's main job.
                                </p>
                                <p>
                                    Concern about space junk goes back to the beginning of the satellite era, but the number of objects in orbit is rising so rapidly that researchers are investigating new ways of attacking the problem. Several teams are trying to improve methods for assessing what is in orbit, so that satellite operators can work more efficiently in ever-more-crowded space. Some researchers are now starting to compile a massive data set that includes the best possible information on where everything is in orbit. Others are developing taxonomies of space – working on measuring properties such as the shape and size of an object, so that satellite operators know how much to worry about what's coming their way.
                                </p>
                                <p>
                                    The alternative, many say, is unthinkable. Just a few uncontrolled space crashes could generate enough debris to set off a runaway cascade of fragments, rendering near-Earth space unusable. 'If we go on like this, we will reach a point of no return,' says Carolin Frueh, an astrodynamical researcher at Purdue University in West Lafayette, Indiana.
                                </p>
                                <p>
                                    Even as our ability to monitor space objects increases, so too does the total number of items in orbit. That means companies, governments and other players in space are collaborating in new ways to avoid a shared threat. International groups such as the Inter-Agency Space Debris Coordination Committee have developed guidelines on space sustainability. Those include inactivating satellites at the end of their useful life by venting pressurised materials or leftover fuel that might lead to explosions. The intergovernmental groups also advise lowering satellites deep enough into the atmosphere that they will burn up or disintegrate within 25 years.
                                </p>
                                <p>
                                    But so far, only about half of all missions have abided by this 25-year goal, says Holger Krag, head of the European Space Agency's space-debris office in Darmstadt, Germany. Operators of the planned large constellations of satellites say they will be responsible stewards in their enterprises in space, but Krag worries that problems could increase, despite their best intentions. 'What happens to those that fail or go bankrupt?' he asks. 'They are probably not going to spend money to remove their satellites from space.'
                                </p>
                                <p>
                                    In theory, given the vastness of space, satellite operators should have plenty of room for all these missions to fly safely without ever nearing another object. So some scientists are tackling the problem of space junk by trying to find out where all the debris is to a high degree of precision. That would alleviate the need for many of the unnecessary manoeuvres that are carried out to avoid potential collisions. 'If you knew precisely where everything was, you would almost never have a problem,' says Marlon Sorge, a space-debris specialist at the Aerospace Corporation in El Segundo, California.
                                </p>
                                <p>
                                    The field is called space traffic management, because it's similar to managing traffic on the roads or in the air. Think about a busy day at an airport, says Moriba Jah, an astrodynamicist at the University of Texas at Austin: planes line up in the sky, landing and taking off close to one another in a carefully choreographed routine. Air-traffic controllers know the location of the planes down to one metre in accuracy. The same can't be said for space debris. Not all objects in orbit are known, and even those included in databases are not tracked consistently.
                                </p>
                                <p>
                                    An additional problem is that there is no authoritative catalogue that accurately lists the orbits of all known space debris. Jah illustrates this with a web-based database that he has developed. It draws on several sources, such as catalogues maintained by the US and Russian governments, to visualise where objects are in space. When he types in an identifier for a particular space object, the database draws a purple line to designate its orbit. Only this doesn't quite work for a number of objects, such as a Russian rocket body designated in the database as object number 32280. When Jah enters that number, the database draws two purple lines: the US and Russian sources contain two completely different orbits for the same object. Jah says that it is almost impossible to tell which is correct, unless a third source of information made it possible to cross-correlate.
                                </p>
                                <p>
                                    Jah describes himself as a space environmentalist: 'I want to make space a place that is safe to operate, that is free and useful for generations to come.' Until that happens, he argues, the space community will continue devolving into a tragedy in which all spaceflight operators are polluting a common resource.
                                </p>
                            </div>
                        </div>
                    </div>




                    {/* Right: Questions */}
                    <div
                        className="w-[50%] flex flex-col items-start bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 h-full max-h-full overflow-y-auto relative"
                        style={{ overflowAnchor: 'none' }}
                    >
                        {/* Questions 32 – 35: Summary */}
                        <div className="w-full">
                            <h3 className="text-[15px] font-bold mb-3 text-left">Questions 32 – 35</h3>
                            <p className="text-[14px] mb-4 text-left">
                                Complete the summary below.<br />
                                Choose <strong>ONE WORD ONLY</strong> from the passage for each answer.
                            </p>

                            <div className="flex flex-col space-y-4 text-[14px] font-medium text-black">
                                <div className="flex flex-wrap items-center gap-1">
                                    <span>The committee gives advice on how the</span>
                                    <input
                                        ref={(el) => (questionRefs.current[31] = el)}
                                        type="text"
                                        placeholder="32"
                                        className="rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[140px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                        value={answers[31] || ""}
                                        onChange={(e) => {
                                            const newAnswers = [...answers];
                                            newAnswers[31] = e.target.value;
                                            setAnswers(newAnswers);
                                        }}
                                    />
                                    <span>of space can be achieved.</span>
                                </div>

                                <div className="flex flex-wrap items-center gap-1">
                                    <span>The committee advises that when satellites are no longer active, any unused</span>
                                    <input
                                        ref={(el) => (questionRefs.current[32] = el)}
                                        type="text"
                                        placeholder="33"
                                        className="rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[140px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                        value={answers[32] || ""}
                                        onChange={(e) => {
                                            const newAnswers = [...answers];
                                            newAnswers[32] = e.target.value;
                                            setAnswers(newAnswers);
                                        }}
                                    />
                                    <span>or pressurised material that could cause</span>
                                    <input
                                        ref={(el) => (questionRefs.current[33] = el)}
                                        type="text"
                                        placeholder="34"
                                        className="rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[140px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                        value={answers[33] || ""}
                                        onChange={(e) => {
                                            const newAnswers = [...answers];
                                            newAnswers[33] = e.target.value;
                                            setAnswers(newAnswers);
                                        }}
                                    />
                                    <span>should be removed.</span>
                                </div>

                                <div className="flex flex-wrap items-center gap-1">
                                    <span>Although operators of large satellite constellations accept that they have obligations as stewards of space, Holger Krag points out that the operators that become</span>
                                    <input
                                        ref={(el) => (questionRefs.current[34] = el)}
                                        type="text"
                                        placeholder="35"
                                        className="rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[140px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                                        value={answers[34] || ""}
                                        onChange={(e) => {
                                            const newAnswers = [...answers];
                                            newAnswers[34] = e.target.value;
                                            setAnswers(newAnswers);
                                        }}
                                    />
                                    <span>are unlikely to prioritise removing their satellites from space.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>



            {/* Main content (scrollable) */}
            <div className="flex-1 flex flex-col items-center justify-start w-full">
                <div ref={highlightRef}
                    className="flex flex-row w-full overflow-hidden -mb-16 mt-2 px-12 pb-20"
                    style={{ height: 'calc(100vh - 180px)' }}
                >
                    {/* Left: Reading Passage */}
                    <div className="bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 w-[49%] h-full max-h-full overflow-y-auto">
                        <div className="mb-8">
                            <h2 className="text-[16px] font-bold mb-4 text-center">I contain multitudes</h2>
                            <h3 className="text-[14px] font-semibold mb-6 text-center italic">Wendy Moore reviews Ed Yong's book about microbes</h3>

                            {/* First paragraph - centered, italic */}
                            <p className="text-[14px] italic mb-6 text-center">
                                Microbes, most of them bacteria, have populated this planet since long before animal life developed and they will outlive us. Invisible to the naked eye, they are ubiquitous. They inhabit the soil, air, rocks and water and are present within every form of life, from seaweed and coral to dogs and humans. And, as Yong explains in his utterly absorbing and hugely important book we mess with them at our peril.
                            </p>

                            {/* Remaining paragraphs - left aligned */}
                            <div className="text-[14px] space-y-4 text-left">
                                <p>
                                    Every species has its own colony of microbes, called a 'microbiome', and these microbes vary not only between species but also between individuals and within different parts of each individual. What is amazing is that while the number of human cells in the average person is about 30 trillion, the number of microbial ones is higher – about 39 trillion. At best, Yong informs us, we are only 50 per cent human. Indeed, some scientists even suggest we should think of each species and its microbes as a single unit, dubbed a 'holobiont'.
                                </p>
                                <p>
                                    In each human there are microbes that live only in the stomach, the mouth or the armpit and by and large they do so peacefully. So 'bad' microbes are just microbes out of context. Microbes that sit contentedly in the human gut (where there are more microbes than there are stars in the galaxy) can become deadly if they find their way into the bloodstream. These communities are constantly changing too. The right-hand shares just one sixth of its microbes with the left hand. And, of course, we are surrounded by microbes. Every time we eat, we swallow a million microbes in each gram of food; we are continually swapping microbes with other humans, pets and the world at large.
                                </p>
                                <p>
                                    It's a fascinating topic and Yong, a young British science journalist, is an extraordinarily adept guide. Writing with lightness and panache, he has a knack of explaining complex science in terms that are both easy to understand and totally enthralling. Yong is on a mission. Leading us gently by the hand, he takes us into the world of microbes – a bizarre, alien planet – in a bid to persuade us to love them as much as he does. By the end, we do.
                                </p>
                                <p>
                                    For most of human history we had no idea that microbes existed. The first man to see these extraordinarily potent creatures was a Dutch lens-maker called Antony van Leeuwenhoek in the 1670s. Using microscopes of his own design that could magnify up to 270 times, he examined a drop of water from a nearby lake and found it teeming with tiny creatures he called 'animalcules'. It wasn't until nearly two hundred years later that the research of French biologist Louis Pasteur indicated that some microbes caused disease. It was Pasteur's 'germ theory' that gave bacteria the poor image that endures today.
                                </p>
                                <p>
                                    Yong's book is in many ways a plea for microbial tolerance, pointing out that while fewer than one hundred species of bacteria bring disease, many thousands more play a vital role in maintaining our health. The book also acknowledges that our attitude towards bacteria is not a simple one. We tend to see the dangers posed by bacteria, yet at the same time we are sold yoghurts and drinks that supposedly nurture 'friendly' bacteria. In reality, says Yong, bacteria should not be viewed as either friends or foes, villains or heroes. Instead, we should realise we have a symbiotic relationship, that can be mutually beneficial or mutually destructive.
                                </p>
                                <p>
                                    What then do these millions of organisms do? The answer is pretty much everything. New research is now unravelling the ways in which bacteria aid digestion, regulate our immune systems, eliminate toxins, produce vitamins, affect our behaviour and even combat obesity. 'They actually help us become who we are,' says Yong. But we are facing a growing problem. Our obsession with hygiene, our overuse of antibiotics and our unhealthy, low-fibre diets are disrupting the bacterial balance and may be responsible for soaring rates of allergies and immune problems, such as inflammatory bowel disease (IBD).
                                </p>
                                <p>
                                    The most recent research actually turns accepted norms upside down. For example, there are studies indicating that the excessive use of household detergents and antibacterial products actually destroys the microbes that normally keep the more dangerous germs at bay. Other studies show that keeping a dog as a pet gives children early exposure to a diverse range of bacteria, which may help protect them against allergies later.
                                </p>
                                <p>
                                    The readers of Yong's book must be prepared for a decidedly unglamorous world. Among the less appealing case studies is one about a fungus that is wiping out entire populations of frogs and that can be halted by a rare microbial bacterium. Another is about squid that carry luminescent bacteria that protect them against predators. However, if you can overcome your distaste for some of the investigations, the reasons for Yong's enthusiasm become clear. The microbial world is a place of wonder. Already, in an attempt to stop mosquitoes spreading dengue fever – a disease that infects 400 million people a year – mosquitoes are being loaded with a bacterium to block the disease. In the future, our ability to manipulate microbes means we could construct buildings with useful microbes built into their walls to fight off infections. Just imagine a neonatal hospital ward coated in a specially mixed cocktail of microbes so that babies get the best start in life.
                                </p>
                            </div>
                        </div>
                    </div>




                    {/* Right: Questions */}
                    <div
                        className="w-[50%] flex flex-col items-start bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 h-full max-h-full overflow-y-auto"
                    >

                        <div className="mb-10 w-full">
                            <h3 className="text-[15px] font-bold mb-4 text-left">Questions 17 – 20</h3>
                            <p className="text-[14px] mb-2 text-left">
                                Complete the summary using the list of words.<br />
                                Choose the correct word and move it into the gap.
                            </p>
                            <h2 className="text-[16px] font-bold text-center mb-2">We should be more tolerant of microbes</h2>
                            <div className="text-[14px] font-medium text-black mb-4">
                                Yong's book argues that we should be more tolerant of microbes. Many have a beneficial effect, and only a relatively small number lead to
                                <DropBox idx={16} />. And although it is misleading to think of microbes as 'friendly', we should also stop thinking of them as the enemy. In fact, we should accept that our relationship with microbes is one based on
                                <DropBox idx={17} />.<br />
                                New research shows that microbes have numerous benefits for humans. Amongst other things, they aid digestion, remove poisons, produce vitamins and may even help reduce obesity. However, there is a growing problem. Our poor
                                <DropBox idx={18} />, our overuse of antibiotics, and our excessive focus on
                                <DropBox idx={19} /> are upsetting the bacterial balance and may be contributing to the huge increase in allergies and immune system problems.
                            </div>
                            <div className="flex flex-wrap gap-2 mt-4">
                                {availableWords.map((word) => (
                                    <div
                                        key={word}
                                        draggable
                                        onDragStart={e => e.dataTransfer.setData('text/plain', word)}
                                        className="cursor-move select-none border border-gray-400 rounded px-3 py-1.5 bg-white shadow-sm text-[14px] font-semibold hover:bg-blue-50 transition-colors"
                                        style={{ minWidth: 80, textAlign: 'center' }}
                                    >
                                        {word}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>









            {/* Bottom Navigation (fixed footer) */}
            <div className="sticky bottom-0 left-0 w-full bg-white border-t border-gray-300 z-30 flex items-center justify-between px-4 py-3">

                {/* Left: Question Buttons */}
                <div className="flex items-center space-x-1">
                    {Array.from({ length: 10 }, (_, i) => (
                        <button
                            key={i + 1}
                            onClick={() => {
                                setCurrentQuestion(i + 1);
                                setTimeout(() => {
                                    const ref = questionRefs.current[i];
                                    if (ref) {
                                        ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                        ref.focus();
                                    }
                                }, 0);
                            }}
                            className={`w-6 h-6 mx-0.5 rounded ${currentQuestion === i + 1 ? 'bg-blue-500 text-white' : 'bg-black text-white'
                                } text-[12px] font-bold flex items-center justify-center`}
                        >
                            {i + 1}
                        </button>
                    ))}
                </div>

                {/* Right: Arrows */}
                <div className="flex items-center space-x-2">
                    {/* Backward Arrow */}
                    <button
                        onClick={() => {
                            setCurrentQuestion((q) => Math.max(1, q - 1));
                            setTimeout(() => {
                                const ref = questionRefs.current[currentQuestion - 2];
                                if (ref) {
                                    ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    ref.focus();
                                }
                            }, 0);
                        }}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-[#2B7BB9] text-white hover:bg-[#2468A0] transition-colors"
                        title="Previous Question"
                        disabled={currentQuestion === 1}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    {/* Forward Arrow */}
                    <button
                        onClick={() => {
                            setCurrentQuestion((q) => Math.min(10, q + 1));
                            setTimeout(() => {
                                const ref = questionRefs.current[currentQuestion];
                                if (ref) {
                                    ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    ref.focus();
                                }
                            }, 0);
                        }}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-[#2B7BB9] text-white hover:bg-[#2468A0] transition-colors"
                        title="Next Question"
                        disabled={currentQuestion === 10}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            </div>


        </div>
    );
};

export default Reading_Class_01;
